import React, { useEffect } from 'react';

import { useRouter } from 'next/router';
import { setSsrTokens } from 'utils/authentication';
import { queryClientConfig } from '@core/configs/queryClientConfig';
import { useQuery, useMutation, dehydrate, QueryClient } from 'react-query';
import { Assessment, GetAssessmentStatus } from 'services/core';
import UseToast from 'hooks/use-toast';
import { getErrorText } from 'utils/geterrortext';

import { NextSeo } from 'next-seo';
import { getAssessmentDynamicProps } from 'router/seoprops';
import ActionButton from '@core/components/actionbutton/ActionButton';
import Spinner from '@core/components/spinner/Spinner';
import LinkButton from '@core/components/linkbutton/LinkButton';
import FAQ from 'components/common/faq/FAQ';
import ProficiencyBadge from '@core/components/proficiencybadge/ProficiencyBadge';
import CareerCard from 'components/pages/career/careercard/CareerCard';

import classes from './assessment.module.scss';


const assessment = (props) => {
    const seoProps = getAssessmentDynamicProps({ ...props });
    const { ToastContainer, toast } = UseToast();
    const router = useRouter();
    const { data, isLoading, refetch } = useQuery(
        ['assessment', router?.query?.id],
        () => Assessment.GET(router?.query?.id),
        {
            enabled: !!router?.query?.id
        }
    );
    const { data: statusData, isLoading: isStatusLoading, refetch: refetchStatus } = useQuery(
        ['assessment-status', router?.query?.id],
        () => GetAssessmentStatus(router?.query?.id),
        {
            cacheTime: 0,
            staleTime: 0,
            enabled: false,
        }
    );

    useEffect(() => {
        const refetchOnFocus = () => {
            setTimeout(refetchStatus, 2000);
        };
        window.addEventListener('focus', refetchOnFocus);
        return () => {
            window.removeEventListener('focus', refetchOnFocus);
        };
    }, []);

    const { mutate, isLoading: isMutating } = useMutation(['enroll-assessment', router?.query?.id], () => Assessment.POST(router?.query?.id), {
        onSuccess: (data) => {
            window.open(data.data.launch_link, '_blank');
        },
        onError: (error) => {
            if (error.response.status === 401) {
                router.push('/login?next=/assessments/' + router?.query?.id);
            }
            else
                toast(getErrorText(error), 'alert');
        }
    });
    return (
        <>
            <NextSeo {...seoProps} />
            <div className="container-fluid p-0">
                {(data && !isLoading) ? (
                    <>
                        <div className='container'>
                            <div className='row justify-content-center mx-1 mt-5'>
                                <div
                                    className={classes['header']}
                                >
                                    <div className='row'>
                                        <div className='col-12 col-md-9'>
                                            <h1>{data.data.title}</h1>
                                            <p>
                                                {data.data.short_introduction}
                                            </p>
                                            {statusData ? (
                                                <>
                                                    {statusData.data.last_status ? (
                                                        <div className={classes['button-group']}>
                                                            <ActionButton key={statusData?.data?.last_status ? 'retake' : 'start'} kind='secondary' loading={isMutating} onClick={() => mutate()}>Retake Assessment</ActionButton>
                                                            <LinkButton kind='primary-color' href={`/assessments/${router?.query?.id}/results`}>View Results</LinkButton>
                                                        </div>
                                                    ) : (
                                                        <div className={classes['button-start']}>
                                                            <ActionButton kind='primary' loading={isMutating} onClick={() => mutate()}>Start Assessment</ActionButton>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    {data.data.last_status ? (
                                                        <div className={classes['button-group']}>
                                                            <ActionButton key={data?.data?.last_status ? 'retake' : 'start'} kind='secondary' loading={isMutating} onClick={() => mutate()}>Retake Assessment</ActionButton>
                                                            <LinkButton kind='primary-color' href={`/assessments/${router?.query?.id}/results`}>View Results</LinkButton>
                                                        </div>
                                                    ) : (
                                                        <div className={classes['button-start']}>
                                                            <ActionButton kind='primary' loading={isMutating} onClick={() => mutate()}>Start Assessment</ActionButton>
                                                        </div>
                                                    )}
                                                </>
                                            )}



                                        </div>
                                        <div className='col-12 col-md-3'>
                                            <div className='d-flex flex-column pt-4'>
                                                <div>
                                                    <div className={classes['info-title']}>Proficiency</div>
                                                    <ProficiencyBadge proficiency={data.data.proficiency} />
                                                </div>
                                                <div className='mt-5'>
                                                    <div className={classes['info-title']}>
                                                        Estimated Length
                                                    </div>
                                                    <div className={classes['info-length']}>
                                                        <div>{data.data.duration} m </div>
                                                        <div>{data.data.question_count} questions</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                                <div className={classes['about-wrapper']}>
                                    <h3>
                                        About this Assessment
                                    </h3>
                                    <div
                                        dangerouslySetInnerHTML={{ __html: data.data.description }}
                                    ></div>
                                </div>
                            </div>
                        </div>
                        <div className="container-fluid p-0">
                            {data.data.career_paths?.length > 0 ? (
                                <>
                                    <div className={classes['related-career-paths']}>
                                        <div className="container">
                                            <h3>Related Career Paths</h3>

                                            <div className={classes['card-wrapper']}>
                                                {data.data.career_paths.map((item, index) => (
                                                    <CareerCard key={index} data={item} />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <></>
                            )}
                        </div>

                        <div className="container my-5">
                            <div>
                                <FAQ data={data.data.faqs} />
                            </div>
                        </div>

                    </>
                ) : (
                    <div
                        className={`${classes['loading']} h-100 d-flex flex-row align-items-center justify-content-center`}>
                        <Spinner />
                    </div>
                )}
            </div >
        </>

    );
};


export async function getServerSideProps(context) {
    setSsrTokens(context);
    const queryClient = new QueryClient(queryClientConfig);

    const { id, slug } = context.params;
    console.log('SSR Params:', id);

    try {
        const response = await Assessment.GET(id);

        await queryClient.prefetchQuery(
            ['assessment', id],
            () => ({ data: response.data })
        );

        // Redirect if slug is missing or incorrect
        if (slug !== response.data.slug) {
            const { ...queryParams } = context.query;
            const newQueryParams = Object.keys(queryParams).length
                ? `?${new URLSearchParams(queryParams).toString()}`
                : '';

            return {
                redirect: {
                    permanent: false,
                    destination: `/assessments/${id}/${response.data.slug}${newQueryParams}`
                }
            };
        }

        return {
            props: {
                dehydratedState: dehydrate(queryClient),
                id,
                title: response.data.title,
                description: response.data.meta_description,
                slug: response.data.slug,
                image: response.data.image
            }
        };
    } catch (e) {
        if (e.response?.status === 404) {
            return {
                redirect: {
                    permanent: false,
                    destination: '/404'
                }
            };
        }

        return { notFound: true };
    }
}

export default assessment;